import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { UntypedFormControl, UntypedFormGroup, NgForm, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { FindControlIdService } from 'webcommon/legacy-common';
import { FindControlBehaviorDto, PrepopulateType } from 'webcommon/shared';
import { ApBaseComponent } from 'webcommon/core-ui';

@Component({
  selector: 'ap-findcontrol-modify-information-tab-component',
  templateUrl: 'ap-findcontrol-modify-information-tab.component.html',
  styleUrls: ['ap-findcontrol-modify-information-tab.component.less'],
})
export class ApFindControlModifyInformationTabComponent extends ApBaseComponent implements OnInit, OnChanges {
  constructor(
    public findControlIdService: FindControlIdService,
  ) {
    super();
  }

  @Input() baseId?: string;
  @Input() isLoading: boolean;
  @Input() findControlBehavior: FindControlBehaviorDto;
  @Input() informationTabFormGroup: UntypedFormGroup;
  @Input() form: NgForm;
  @Output() errorCountChange: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('findControlIdRef') findControlIdRef: ElementRef;
  @ViewChild('findControlNameRef') findControlNameRef: ElementRef;
  @ViewChild('findControlLicenseCodeRef') findControlLicenseCodeRef: ElementRef;
  @ViewChild('findControlSecurityGroupRef') findControlSecurityGroupRef: ElementRef;
  @ViewChild('findControlFindTableNameRef') findControlFindTableNameRef: ElementRef;
  @ViewChild('numericUpDownRef') numericUpDownRef: ElementRef;
  @ViewChild('findControlNotesRef') findControlNotesRef: ElementRef;

  Id = '';

  externalIdFormControl = new UntypedFormControl(null);
  nameFormControl = new UntypedFormControl(null);
  licenseCodeFormControl = new UntypedFormControl(null);
  findTableNameFormControl = new UntypedFormControl(null);
  inactiveFormControl = new UntypedFormControl(null);
  userDefinedFormControl = new UntypedFormControl(null);
  prepopulateOptionsFormControl = new UntypedFormControl(null);
  maxRowsFormControl = new UntypedFormControl(null);
  notesFormControl = new UntypedFormControl(null);
  columnsConfigFormControl = new UntypedFormControl(null);

  isSystemDefinedFindControl = true;

  // Find Control Ids
  securityGroupUid: string | null = '';
  addInUid: string | null = '';

  ngOnInit() {
    this.setId();
  }

  ngOnChanges(changes: SimpleChanges) {
    const changedFindControlBehavior = changes.findControlBehavior && changes.findControlBehavior.currentValue;
    if (changedFindControlBehavior) {
      this.isSystemDefinedFindControl = (this.findControlBehavior.UserDefined !== 1);
      this.setupFormGroup();
    }
  }

  public setupFormGroup(): void {
    // Possible values and meaning for UserDefined are:
    //   0 = System Defined (no modifications)
    //   1 = User Defined
    //   2 = System Defined (modified)
    //
    // Various input controls are only enabled if the ListFindControlBehavior
    // item is user defined, findControlBehavior.UserDefined ==  1

    this.externalIdFormControl.setValidators([Validators.maxLength(25)]);
    this.externalIdFormControl.setValue(this.findControlBehavior.ExternalId);
    this.informationTabFormGroup.addControl('externalIdFormControl', this.externalIdFormControl);

    this.nameFormControl.setValidators([Validators.maxLength(50), Validators.required]);
    this.nameFormControl.setValue(this.findControlBehavior.Name);
    // if (this.isSystemDefinedFindControl) this.nameFormControl.disable();
    this.informationTabFormGroup.addControl('nameFormControl', this.nameFormControl);

    this.securityGroupUid = this.findControlBehavior.SecurityGroupUid;

    this.licenseCodeFormControl.setValidators([Validators.maxLength(50)]);
    this.licenseCodeFormControl.setValue(this.findControlBehavior.LicenseCode);
    // if (this.isSystemDefinedFindControl) this.licenseCodeFormControl.disable();
    this.informationTabFormGroup.addControl('licenseCodeFormControl', this.licenseCodeFormControl);

    this.findTableNameFormControl.setValidators([Validators.maxLength(50), Validators.required]);
    this.findTableNameFormControl.setValue(this.findControlBehavior.Id);
    // if (this.isSystemDefinedFindControl) this.findTableNameFormControl.disable();
    this.informationTabFormGroup.addControl('findTableNameFormControl', this.findTableNameFormControl);

    this.addInUid = this.findControlBehavior.AddInUid;

    this.inactiveFormControl.setValue(this.findControlBehavior.Inactive);
    this.informationTabFormGroup.addControl('inactiveFormControl', this.inactiveFormControl);

    this.userDefinedFormControl.setValue(!this.isSystemDefinedFindControl);
    this.userDefinedFormControl.disable();
    this.informationTabFormGroup.addControl('userDefinedFormControl', this.userDefinedFormControl);

    this.prepopulateOptionsFormControl.setValue(this.findControlBehavior.Prepopulate);
    this.informationTabFormGroup.addControl('prepopulateOptionsFormControl', this.prepopulateOptionsFormControl);

    this.maxRowsFormControl.setValidators([Validators.required]);
    this.maxRowsFormControl.setValue(this.findControlBehavior.MaxRows);
    this.informationTabFormGroup.addControl('maxRowsFormControl', this.maxRowsFormControl);

    this.notesFormControl.setValue(this.findControlBehavior.Notes);
    this.informationTabFormGroup.addControl('notesFormControl', this.notesFormControl);

    this.columnsConfigFormControl.setValue(this.findControlBehavior.ColumnsConfig);
    this.columnsConfigFormControl.disable();
    this.informationTabFormGroup.addControl('columnsConfigFormControl', this.columnsConfigFormControl);

    if (this.isSystemDefinedFindControl) {
      this.nameFormControl.disable();
      this.licenseCodeFormControl.disable();
      this.findTableNameFormControl.disable();
    }

    // statusChanges emits an event when the validation status is recalculated.
    this.externalIdFormControl.statusChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(() => {
      // emit validation error count when validation status is recalcaluated.
      this.emitValidationErrorCount();
    });

    // statusChanges emits an event when the validation status is recalculated.
    this.maxRowsFormControl.statusChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(() => {
      // emit validation error count when validation status is recalcaluated.
      this.emitValidationErrorCount();
    });
  }

  public scrollToValidationError(): void {
    const options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' };

    if (this.externalIdFormControl.invalid) {
        this.findControlIdRef.nativeElement.scrollIntoView(options);
    } else if (this.nameFormControl.invalid) {
        this.findControlNameRef.nativeElement.scrollIntoView(options);
    } else if (this.licenseCodeFormControl.invalid) {
        this.findControlLicenseCodeRef.nativeElement.scrollIntoView(options);
    } else if (!this.securityGroupUid || this.securityGroupUid === '') {
        this.findControlSecurityGroupRef.nativeElement.scrollIntoView(options);
    } else if (this.findTableNameFormControl.invalid) {
        this.findControlFindTableNameRef.nativeElement.scrollIntoView(options);
    } else if (this.maxRowsFormControl.invalid) {
        this.numericUpDownRef.nativeElement.scrollIntoView(options);
    } else if (this.notesFormControl.invalid) {
        this.findControlNotesRef.nativeElement.scrollIntoView(options);
    }
  }

  public emitValidationErrorCount(): void {
    let errorCount = 0;
    Object.keys(this.informationTabFormGroup.controls).forEach(key => {
      if (this.informationTabFormGroup.get(key)?.invalid) {
        errorCount++;
      }
    });
    this.errorCountChange.emit(errorCount);
  }

  public save(): FindControlBehaviorDto {
    const dto = new FindControlBehaviorDto();

    dto.FindControlBehaviorUid = this.findControlBehavior.FindControlBehaviorUid;
    dto.ExternalId = this.externalIdFormControl.value as string;
    dto.Name = this.nameFormControl.value as string;
    dto.SecurityGroupUid = this.securityGroupUid;
    dto.LicenseCode = this.licenseCodeFormControl.value as string;
    dto.Id = this.findTableNameFormControl.value as string;
    dto.AddInUid = this.addInUid;
    dto.Inactive = this.inactiveFormControl.value as boolean;
    dto.UserDefined = this.findControlBehavior.UserDefined;
    dto.Prepopulate = this.prepopulateOptionsFormControl.value as PrepopulateType;
    dto.MaxRows = this.maxRowsFormControl.value as number;
    dto.Notes = this.notesFormControl.value as string;
    dto.ColumnsConfig = this.columnsConfigFormControl.value as string;

    dto.FindTableUid = this.findControlBehavior.FindTableUid;
    dto.HasSecurity = this.findControlBehavior.HasSecurity;
    dto.AllowMaintenance = this.findControlBehavior.AllowMaintenance;

    return dto;
  }

  private setId() {
    this.Id = `${this.baseId}-information-tab`;
  }
}
